import { captureException } from '@sentry/react';
import { useRef, useState } from 'react';
import { flushSync } from 'react-dom';
import { toast } from 'sonner';

import {
  omittedPartsWarning,
  renderMessagePng,
  type OmittedPart,
} from './message-image-capture';

const COPIED_LABEL_DURATION_MS = 1500;

export const useCopyAsImage = (onCopied: () => void) => {
  const imageRef = useRef<HTMLDivElement>(null);
  const [isRendering, setIsRendering] = useState(false);
  const [isCopied, setIsCopied] = useState(false);

  const copyAsImage = async () => {
    if (isRendering) return;
    flushSync(() => setIsRendering(true));
    const omittedParts: OmittedPart[] = [];
    try {
      if (!imageRef.current) throw new Error('Message image did not mount');
      const png = renderMessagePng(imageRef.current, (part) =>
        omittedParts.push(part)
      );
      await navigator.clipboard.write([
        new ClipboardItem({ 'image/png': png }),
      ]);
      setIsCopied(true);
      setTimeout(() => setIsCopied(false), COPIED_LABEL_DURATION_MS);
      onCopied();
      if (omittedParts.length > 0) {
        toast.warning(omittedPartsWarning(omittedParts));
      }
    } catch (error) {
      captureException(error);
      toast.error("Couldn't copy response as image");
    } finally {
      setIsRendering(false);
    }
  };

  return { imageRef, isRendering, isCopied, copyAsImage };
};
